import { ref, watch, toValue, computed, effectScope } from 'vue';

import { useAuthentication } from './useAuthentication';

const StorageType = Object.freeze({
    Local: 'localStorage',
    Session: 'sessionStorage',
});

// One shared ref per storage key, i.e. { data, defaultValue }
const cache = new Map();

// The shared refs outlive the component that first reads them, so their persistence watchers run in a detached
// scope (otherwise they're bound to that component's scope and silently stop persisting when it unmounts)
const scope = effectScope(true);

// Checks that a stored value has the same shape as the default (object, array or primitive type), so corrupted or
// legacy values (e.g. a string or null where an object is expected) can't break callers that set properties on it
const isSameType = (value, defaultValue) => {
    if (defaultValue === null || defaultValue === undefined) {
        return true;
    }
    if (Array.isArray(defaultValue)) {
        return Array.isArray(value);
    }
    if (typeof defaultValue === 'object') {
        return value !== null && typeof value === 'object' && !Array.isArray(value);
    }
    return typeof value === typeof defaultValue;
};

// Defaults are copied so keys never share (and mutate) the same default object
const cloneDefault = (value) => (value !== null && typeof value === 'object' ? structuredClone(value) : value);

// Accessing web storage can throw (e.g. when the browser blocks site data), in which case settings are kept in memory only
const resolveStorage = (type) => {
    try {
        return window[type] ?? null;
    } catch {
        return null;
    }
};

const load = (storage, k, defaultValue) => {
    try {
        const raw = storage?.getItem(k) ?? null;
        if (raw === null) {
            return cloneDefault(defaultValue);
        }
        const value = JSON.parse(raw);
        if (!isSameType(value, defaultValue)) {
            console.warn(`Ignoring invalid stored value for '${k}': '${raw}'`);
            return cloneDefault(defaultValue);
        }
        return value;
    } catch {
        return cloneDefault(defaultValue);
    }
};

const save = (storage, k, value) => {
    try {
        if (value === null || value === undefined) {
            storage?.removeItem(k);
        } else {
            storage?.setItem(k, JSON.stringify(value));
        }
    } catch (error) {
        // e.g. QuotaExceededError - the in-memory value is still updated, it just won't persist
        console.warn(`Unable to save setting '${k}'`, error);
    }
};

const getCacheKey = (type, k) => `${type}:${k}`;

const getStoredRef = (type, k, defaultValue) => {
    const cacheKey = getCacheKey(type, k);
    let entry = cache.get(cacheKey);
    if (!entry) {
        const storage = resolveStorage(type);
        const data = ref(load(storage, k, defaultValue));
        scope.run(() => watch(data, (value) => save(storage, k, value), { deep: true }));
        entry = { data, defaultValue };
        cache.set(cacheKey, entry);
    }
    return entry.data;
};

// Keep shared refs in sync with changes made in other tabs (local storage only; session storage is per tab).
// Writing the same value back is a no-op that doesn't fire another storage event, so this can't loop.
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
        const storage = resolveStorage(StorageType.Local);
        if (e.key === null || e.storageArea !== storage) {
            return;
        }
        const entry = cache.get(getCacheKey(StorageType.Local, e.key));
        if (entry) {
            entry.data.value = load(storage, e.key, entry.defaultValue);
        }
    });
}

function useWebStorage(key, defaultValue, type) {
    // Used while the key is unresolved (e.g. no signed-in user) - kept in memory only, not persisted
    const fallback = ref(cloneDefault(defaultValue));
    const get = (k) => (k === null || k === undefined ? fallback : getStoredRef(type, k, defaultValue));

    // For a static key, return the ref directly for a simpler usage
    if (typeof key !== 'object' && typeof key !== 'function') {
        return get(key);
    }

    // For a reactive key, return a computed that transparently proxies the current ref
    return computed({
        get: () => get(toValue(key)).value,
        set: (value) => {
            get(toValue(key)).value = value;
        },
    });
}

export const useLocalStorage = (key, defaultValue) => useWebStorage(key, defaultValue, StorageType.Local);
export const useSessionStorage = (key, defaultValue) => useWebStorage(key, defaultValue, StorageType.Session);

export function useUserStorage(key, defaultValue) {
    const { uid } = useAuthentication();

    return useLocalStorage(() => (uid.value ? `${uid.value}:${key}` : null), defaultValue);
}
